import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from "@mui/material"
import { useState } from "react"
import { AppId } from "../../../../app/appTypes"
import { useAppDispatch } from "../../../../app/hooks"
import { showError, showSuccess } from "../../../notifications/notificationsSlice"
import { SaveButton } from "../../../../app/components/SaveButton"
import { CancelButton } from "../../../../app/components/CancelButton"
import { DocumentAnalyzedObject, loadProcessesForACP, LoadProcessesForACPPayload } from "../documentQuestionsApi"

export interface LoadProcessesForACPDialogProps {
    documentId: AppId
    sectionId: string
    onSuccessfulSave: (analysedObjects: DocumentAnalyzedObject[]) => void
    onCancelClick: () => void,
}

export const LoadProcessesForACPDialog = ({ documentId, sectionId, onCancelClick, onSuccessfulSave }: LoadProcessesForACPDialogProps) => {
    const dispatch = useAppDispatch()

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState("")
    const [overwriteExisting, setOverwriteExisting] = useState(true)
    const [onlyProcessesFromCriticalModules, setOnlyProcessesFromCriticalModules] = useState(true)

    const handleOkClick = async () => {
        setSaving(true);
        try {
            const params: LoadProcessesForACPPayload = {
                overwriteExisting,
                onlyProcessesFromCriticalModules,
            };

            const result = await dispatch(
                loadProcessesForACP({ documentId, sectionId, params })
            );

            if (loadProcessesForACP.fulfilled.match(result)) {
                const analysedObjects = result.payload as DocumentAnalyzedObject[];
                dispatch(showSuccess("saved"));
                onSuccessfulSave(analysedObjects);
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title
                    }
                }
                setServerError(errorMsg)
            }

        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Dialog key="load" open={true} maxWidth="xs" fullWidth keepMounted={false}>
                <DialogTitle
                    sx={{
                        bgcolor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75,
                    }}
                >
                    <Localized id="document-analyzed-objects-load-processes">
                        <span>_load</span>
                    </Localized>
                </DialogTitle>

                <DialogContent dividers>
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            alignItems: "center",
                            ".MuiAlert-message": { width: "100%" },
                        }}
                    >
                        <Localized id="document-analyzed-objects-load-processes-desc">
                            <span>_load_desc</span>
                        </Localized>
                    </Alert>
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={overwriteExisting}
                                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                                />
                            }
                            label={
                                <Localized id="overwriteExisting">
                                    <span>_Nadpisz istniejące procesy</span>
                                </Localized>
                            }
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={onlyProcessesFromCriticalModules}
                                    onChange={(e) => setOnlyProcessesFromCriticalModules(e.target.checked)}
                                />
                            }
                            label={
                                <Localized id="onlyProcessesFromCriticalModules">
                                    <span>_Tylko procesy z krytyczne modułów</span>
                                </Localized>
                            }
                        />
                    </Box>

                    {serverError && (
                        <Alert sx={{ marginTop: 1 }} severity="error">
                            <Localized id={serverError}>
                                <span style={{ whiteSpace: 'pre-line' }}>_{serverError}</span>
                            </Localized>
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                    <SaveButton loading={saving} onClick={handleOkClick} />
                    <CancelButton loading={saving} onClick={onCancelClick} />
                </DialogActions>
            </Dialog>
        </>
    )
}
